import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Container,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
} from "@mui/material";
import { RefreshOutlined, NotificationsNoneRounded } from "@mui/icons-material";

import CustomAppBar from "../../../components/CustomAppBar";
import AppPageHeader from "../../../components/AppPageHeader";
import NotificationCard from "../components/NotificationCard";
import { customerService } from "../services/customerService";
import {
  bgGradient,
  textPrimary,
  textSecondary,
  cardShadow,
  STORAGE_KEYS,
  MESSAGES,
} from "../../../constants/consts";

export default function CustomerNotifications({ user, onLogout }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const sunucuKodu =
    user?.sunucu_kodu ||
    localStorage.getItem(STORAGE_KEYS.SUNUCU_KODU) ||
    sessionStorage.getItem(STORAGE_KEYS.SUNUCU_KODU) ||
    "";
  const userType =
    user?.user_type ||
    localStorage.getItem(STORAGE_KEYS.USER_TYPE) ||
    sessionStorage.getItem(STORAGE_KEYS.USER_TYPE) ||
    "user";

  const loadNotifications = useCallback(async () => {
    if (!sunucuKodu) {
      setError(MESSAGES.NOTIFICATION_LOAD_ERROR);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await customerService.getNotifications(sunucuKodu, userType);
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError(MESSAGES.NOTIFICATION_LOAD_ERROR);
    } finally {
      setLoading(false);
    }
  }, [sunucuKodu, userType]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: bgGradient,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.10)",
          top: -100,
          right: -100,
        },
        "&::after": {
          content: '""',
          position: "absolute",
          width: 300,
          height: 300,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.08)",
          bottom: -80,
          left: -80,
        },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <CustomAppBar
          title={MESSAGES.NOTIFICATION_APPBAR}
          isNotHome
          onLogout={onLogout}
          hideNotifications // ← bu sayfadayken zile gerek yok
          extraActions={
            <IconButton
              onClick={loadNotifications}
              title={MESSAGES.NOTIFICATION_REFRESH}
              disabled={loading}
              sx={{
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.12)",
                backdropFilter: "blur(8px)",
                mr: 1,
                "&:hover": { backgroundColor: "rgba(255,255,255,0.22)" },
              }}
            >
              <RefreshOutlined />
            </IconButton>
          }
        />

        <Container>
          <AppPageHeader
            title={MESSAGES.NOTIFICATION_TITLE}
            subtitle={MESSAGES.NOTIFICATION_SUBTITLE}
            onDark
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: "#fff" }} />
            </Box>
          ) : list.length === 0 ? (
            <EmptyState
              title={MESSAGES.NOTIFICATION_EMPTY_TITLE}
              message={MESSAGES.NOTIFICATION_EMPTY_MESSAGE}
            />
          ) : (
            <Stack spacing={2}>
              {list.map((n) => (
                <NotificationCard key={n.id} notification={n} />
              ))}
            </Stack>
          )}
        </Container>
      </Box>
    </Box>
  );
}

function EmptyState({ title, message }) {
  return (
    <Box
      sx={{
        p: 5,
        textAlign: "center",
        borderRadius: 3,
        backgroundColor: "rgba(255,255,255,0.94)",
        boxShadow: cardShadow,
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          mx: "auto",
          mb: 2,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(0,0,0,0.04)",
        }}
      >
        <NotificationsNoneRounded sx={{ fontSize: 36, color: textSecondary }} />
      </Box>
      <Typography
        variant="h6"
        fontWeight={700}
        color={textPrimary}
        gutterBottom
      >
        {title}
      </Typography>
      <Typography variant="body2" color={textSecondary}>
        {message}
      </Typography>
    </Box>
  );
}
